/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  ViewChildren,
  ViewEncapsulation
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import type { PDFDocumentProxy, RenderTask } from 'pdfjs-dist';
import { allFields, FormDefinition, FormField, FormPlace, paperRows, takesValue } from './forms.model';
import { MARK_PARTS, MAX_PLACE_SIZE, MIN_PLACE_SIZE, newPlace, partsOf, PLACE_SIZE, PlacePart, samePart } from './forms.paper';
import { FormsService } from './forms.service';

/** A place and whose it is: a field's, or one of the paper's own marks. */
interface Placed {
  field: FormField | null;
  place: FormPlace;
}

/** The pages' PDFs, by node, so going back to this tab does not fetch them again. */
const papers = new Map<string, Uint8Array>();

/** Remembers a paper just picked, so the editor shows it without fetching it back. */
export const rememberPaper = (nodeId: string, pdf: Uint8Array): void => void papers.set(nodeId, pdf);

/**
 * Placing a form's fields on the organisation's own paper. The paper's pages
 * are drawn by pdf.js; each field's places are boxes over them, dragged into
 * the paper's blanks and sized by their corner. Positions are kept in points
 * from the page's top-left corner, so the zoom only changes how big they look.
 */
@Component({
  selector: 'app-form-paper',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, TranslatePipe],
  encapsulation: ViewEncapsulation.None,
  templateUrl: './form-paper.component.html',
  styleUrls: ['./form-paper.component.scss']
})
export class FormPaperComponent implements OnChanges, OnDestroy {
  private readonly forms = inject(FormsService);
  private readonly translate = inject(TranslateService);
  private readonly host = inject(ElementRef<HTMLElement>);

  @Input({ required: true }) form!: FormDefinition;
  /** Print a test copy with sample values in every place. */
  @Output() readonly testPrint = new EventEmitter<void>();

  @ViewChildren('pageCanvas') private readonly canvases!: QueryList<ElementRef<HTMLCanvasElement>>;

  readonly minSize = MIN_PLACE_SIZE;
  readonly maxSize = MAX_PLACE_SIZE;
  readonly markParts = MARK_PARTS;

  /** Screen pixels for each point of the paper. */
  readonly scale = signal(1.25);
  readonly loading = signal(true);
  readonly failed = signal(false);
  /** The page new places go on. */
  targetPage = 1;
  selected: Placed | null = null;

  private doc: PDFDocumentProxy | null = null;
  private tasks: RenderTask[] = [];
  private loadedFor = '';
  private drag: { placed: Placed; resize: boolean; startX: number; startY: number; from: FormPlace } | null = null;

  get pages(): { width: number; height: number }[] {
    return this.form.paper?.pages ?? [];
  }

  get fields(): FormField[] {
    return allFields(this.form).filter(takesValue);
  }

  ngOnChanges(): void {
    const nodeId = this.form.paper?.nodeId ?? '';
    if (nodeId && nodeId !== this.loadedFor) {
      this.loadedFor = nodeId;
      void this.load(nodeId);
    }
  }

  ngOnDestroy(): void {
    this.cancelRenders();
    void this.doc?.destroy();
  }

  // ── the paper's pages ─────────────────────────────────────────────────

  private async load(nodeId: string): Promise<void> {
    this.loading.set(true);
    this.failed.set(false);
    try {
      let bytes = papers.get(nodeId);
      if (!bytes) {
        bytes = new Uint8Array(
          await new Promise<ArrayBuffer>((resolve, reject) => this.forms.paperPdf(nodeId).subscribe({ next: resolve, error: reject }))
        );
        papers.set(nodeId, bytes);
      }
      const pdfjs = await import('pdfjs-dist');
      pdfjs.GlobalWorkerOptions.workerSrc = `./pdf.worker.min.mjs?v=${pdfjs.version}`;
      void this.doc?.destroy();
      // pdf.js takes the buffer it is given; the cached copy stays whole
      this.doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
      this.loading.set(false);
      setTimeout(() => void this.render());
    } catch {
      this.loading.set(false);
      this.failed.set(true);
    }
  }

  private cancelRenders(): void {
    this.tasks.forEach((t) => t.cancel());
    this.tasks = [];
  }

  private async render(): Promise<void> {
    const doc = this.doc;
    if (!doc || !this.canvases) {
      return;
    }
    this.cancelRenders();
    const ratio = window.devicePixelRatio || 1;
    const canvases = this.canvases.toArray();
    for (let n = 1; n <= Math.min(doc.numPages, canvases.length); n++) {
      const page = await doc.getPage(n);
      const viewport = page.getViewport({ scale: this.scale() * ratio });
      const canvas = canvases[n - 1].nativeElement;
      const context = canvas.getContext('2d');
      if (!context) {
        return;
      }
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const task = page.render({ canvasContext: context, viewport } as any);
      this.tasks.push(task);
      await task.promise.catch(() => undefined);
    }
  }

  zoom(by: number): void {
    this.scale.set(Math.min(2.5, Math.max(0.5, Math.round((this.scale() + by) * 100) / 100)));
    setTimeout(() => void this.render());
  }

  // ── the list of what can be placed ────────────────────────────────────

  partsOf(field: FormField): PlacePart[] {
    return partsOf(field);
  }

  /** The parts are made afresh on every check; without these their buttons are replaced between a press and its release, and the click is lost. */
  readonly trackPart = (_: number, part: PlacePart): string => `${part.part}:${part.option ?? ''}`;
  readonly trackField = (_: number, field: FormField): FormField => field;

  partLabel(field: FormField | null, part: { part?: string; option?: string }): string {
    const name = part.part ?? 'value';
    if (name === 'option') {
      return this.translate.instant('DMS.FORMS.PART_OPTION', { option: part.option ?? '' });
    }
    if (name === 'value' && field) {
      return this.translate.instant(field.type === 'checkbox' ? 'DMS.FORMS.PART_TICK' : 'DMS.FORMS.PART_VALUE');
    }
    return this.translate.instant(`DMS.FORMS.PART_${name.toUpperCase()}${field ? '' : '_MARK'}`);
  }

  placesOf(field: FormField | null, part: PlacePart): FormPlace[] {
    return (field ? (field.places ?? []) : (this.form.paper?.marks ?? [])).filter((p) => samePart(p, part));
  }

  /** A new box for the part on the chosen page, a little below the last one so they do not stack up. */
  place(field: FormField | null, part: PlacePart): void {
    const paper = this.form.paper;
    const page = this.pages[this.targetPage - 1];
    if (!paper || !page) {
      return;
    }
    const placed = this.allPlaced().filter((p) => p.place.page === this.targetPage).length;
    const place = newPlace(field, part, this.targetPage, { x: page.width / 2, y: page.height * 0.15 + (placed % 25) * 24 });
    if (field) {
      field.places = [...(field.places ?? []), place];
    } else {
      paper.marks = [...(paper.marks ?? []), place];
    }
    this.select({ field, place });
  }

  /** Shows a part's first box. */
  show(field: FormField | null, part: PlacePart): void {
    const place = this.placesOf(field, part)[0];
    if (place) {
      this.select({ field, place });
    }
  }

  private allPlaced(): Placed[] {
    return [
      ...(this.form.paper?.marks ?? []).map((place) => ({ field: null, place })),
      ...this.fields.flatMap((field) => (field.places ?? []).map((place) => ({ field, place })))
    ];
  }

  placedOn(page: number): Placed[] {
    return this.allPlaced().filter((p) => p.place.page === page);
  }

  readonly trackPlaced = (_: number, p: Placed): FormPlace => p.place;

  // ── a box ─────────────────────────────────────────────────────────────

  select(placed: Placed): void {
    this.selected = placed;
    this.targetPage = placed.place.page;
    setTimeout(() => {
      const box = this.host.nativeElement.querySelector('.aca-frp__box--on') as HTMLElement | null;
      box?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      box?.focus({ preventScroll: true });
    });
  }

  isSelected(placed: Placed): boolean {
    return this.selected?.place === placed.place;
  }

  remove(): void {
    const s = this.selected;
    if (!s) {
      return;
    }
    if (s.field) {
      s.field.places = (s.field.places ?? []).filter((p) => p !== s.place);
    } else if (this.form.paper) {
      this.form.paper.marks = this.form.paper.marks.filter((p) => p !== s.place);
    }
    this.selected = null;
  }

  /** What the box shows: what will be printed there, roughly. */
  sample(placed: Placed): string {
    const { field, place } = placed;
    const part = place.part ?? 'value';
    if (part === 'option' || (part === 'value' && field?.type === 'checkbox')) {
      return '✓';
    }
    if (part === 'value' && field) {
      return field.label || field.key;
    }
    return this.partLabel(field, place);
  }

  isTick(placed: Placed): boolean {
    return placed.place.part === 'option' || (placed.field?.type === 'checkbox' && (placed.place.part ?? 'value') === 'value');
  }

  isTable(placed: Placed): boolean {
    return placed.field?.type === 'table' && (placed.place.part ?? 'value') === 'value';
  }

  /** The cells of a boxed ID, drawn as lines in the box so they can be matched to the paper's. */
  cellLines(placed: Placed): string {
    const cells = placed.place.cells ?? 0;
    if (cells < 2) {
      return '';
    }
    const step = 100 / cells;
    return `repeating-linear-gradient(90deg, transparent 0 calc(${step}% - 1px), rgba(37, 99, 235, 0.45) calc(${step}% - 1px) ${step}%)`;
  }

  rowLines(placed: Placed): string {
    const height = (placed.place.rowHeight ?? 18) * this.scale();
    return `repeating-linear-gradient(180deg, transparent 0 ${height - 1}px, rgba(13, 148, 136, 0.5) ${height - 1}px ${height}px)`;
  }

  boxLabel(placed: Placed): string {
    return `${placed.field ? placed.field.label || placed.field.key : this.translate.instant('DMS.FORMS.PAPER_MARKS')} · ${this.partLabel(placed.field, placed.place)}`;
  }

  startDrag(event: PointerEvent, placed: Placed, resize: boolean): void {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.select(placed);
    this.drag = { placed, resize, startX: event.clientX, startY: event.clientY, from: { ...placed.place } };
  }

  @HostListener('document:pointermove', ['$event'])
  onMove(event: PointerEvent): void {
    const drag = this.drag;
    if (!drag) {
      return;
    }
    const page = this.pages[drag.placed.place.page - 1];
    if (!page) {
      return;
    }
    const dx = (event.clientX - drag.startX) / this.scale();
    const dy = (event.clientY - drag.startY) / this.scale();
    const place = drag.placed.place;
    if (drag.resize) {
      place.w = round(clamp(drag.from.w + dx, 4, page.width - place.x));
      place.h = round(clamp(drag.from.h + dy, 4, page.height - place.y));
    } else {
      place.x = round(clamp(drag.from.x + dx, 0, page.width - place.w));
      place.y = round(clamp(drag.from.y + dy, 0, page.height - place.h));
    }
  }

  @HostListener('document:pointerup')
  endDrag(): void {
    this.drag = null;
  }

  /** Arrows move the box a point at a time (ten with Shift); with Alt they size it. Delete takes it away. */
  onKey(event: KeyboardEvent, placed: Placed): void {
    const step = event.shiftKey ? 10 : 1;
    const moves: Record<string, [number, number]> = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.select(placed);
      this.remove();
      return;
    }
    const move = moves[event.key];
    const page = this.pages[placed.place.page - 1];
    if (!move || !page) {
      return;
    }
    event.preventDefault();
    const place = placed.place;
    if (event.altKey) {
      place.w = round(clamp(place.w + move[0], 4, page.width - place.x));
      place.h = round(clamp(place.h + move[1], 4, page.height - place.y));
    } else {
      place.x = round(clamp(place.x + move[0], 0, page.width - place.w));
      place.y = round(clamp(place.y + move[1], 0, page.height - place.h));
    }
  }

  // ── the selected box's properties ─────────────────────────────────────

  /** Keeps a typed number inside the page. */
  tidy(place: FormPlace): void {
    const page = this.pages[place.page - 1] ?? this.pages[0];
    if (!page) {
      return;
    }
    place.page = Math.min(Math.max(1, Math.round(place.page || 1)), this.pages.length);
    place.w = round(clamp(place.w || 4, 4, page.width));
    place.h = round(clamp(place.h || 4, 4, page.height));
    place.x = round(clamp(place.x || 0, 0, page.width - place.w));
    place.y = round(clamp(place.y || 0, 0, page.height - place.h));
    if (place.size !== undefined && place.size !== null) {
      place.size = clamp(place.size, MIN_PLACE_SIZE, MAX_PLACE_SIZE);
    }
    if (!place.cells || place.cells < 2) {
      delete place.cells;
    } else {
      place.cells = Math.min(Math.round(place.cells), 40);
    }
    if (place.rowHeight !== undefined) {
      place.rowHeight = clamp(place.rowHeight || 18, 8, 200);
    }
  }

  takesText(placed: Placed): boolean {
    return !this.isTick(placed) && placed.field?.type !== 'signature' && !this.isTable(placed);
  }

  size(place: FormPlace): number {
    return place.size ?? PLACE_SIZE;
  }

  widthsText(place: FormPlace): string {
    return (place.widths ?? []).join(', ');
  }

  /** "1, 1, 1": what equal columns would be written as. */
  evenWidths(table: FormField): string {
    return (table.columns ?? []).map(() => 1).join(', ');
  }

  setWidths(field: FormField, place: FormPlace, text: string): void {
    const widths = text
      .split(/[,\s]+/)
      .map((w) => Number(w))
      .filter((w) => Number.isFinite(w) && w > 0);
    if (widths.length === (field.columns ?? []).length) {
      place.widths = widths;
    } else {
      delete place.widths;
    }
  }

  /** How many rows fit in this table's places, altogether. */
  rowsFit(field: FormField): number {
    return paperRows(this.form, field) ?? 0;
  }
}

const clamp = (n: number, min: number, max: number): number => Math.min(Math.max(n, min), Math.max(min, max));
const round = (n: number): number => Math.round(n * 100) / 100;
